<template>
    <div>
        <ModuleTitleNav :next-module="nextModule" :previous-module="previousModule" class="title-one">
            {{ moduleTitle }}
        </ModuleTitleNav>
        <div class="section-line" />
        <fieldset>
            <b-form-row>
                <b-col>
                    <div class="info-text-section-marker">
                        [Sec 134(b)(1)]
                    </div>
                    <div class="info-text">
                        Describe thoroughly the results of the comprehensive needs
                        assessment conducted and how needs were prioritized.
                    </div>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col>
                    <div class="field-labels">
                        Student Performance Analysis Findings
                    </div>

                    <b-form-textarea
                        v-model="studentPerformanceAnalysis"
                        :disabled="formDisabled"
                        :rows="3"
                    />
                </b-col>
            </b-form-row>
            <br>
            <div class="dotted-rule" />
            <br>

            <b-form-row>
                <b-col>
                    <div class="field-labels">
                        Other Needs Assessment Findings
                    </div>

                    <b-form-textarea
                        v-model="otherNeedsAssessment"
                        :disabled="formDisabled"
                        :rows="3"
                    />
                </b-col>
            </b-form-row>
            <br>
            <div class="dotted-rule" />
            <br>

            <b-form-row>
                <b-col>
                    <div class="field-labels">
                        How Needs Were Prioritized
                    </div>

                    <b-form-textarea
                        v-model="prioritized"
                        :disabled="formDisabled"
                        :rows="3"
                    />
                </b-col>
            </b-form-row>
            <br>
            <div class="dotted-rule" />
            <br>

            <b-form-row>
                <b-col>
                    <div class="info-text-section-marker">
                        [Sec 134(b)(2)]
                    </div>
                    <div class="info-text">
                        Activities Supported with Perkins Funds
                    </div>
                    <div class="info-text">
                        Which programs and services will be supported (Focus Areas) by Perkins funds during
                        July 1, 2020 through June 30, 2024?
                        These should be general areas from which specific activities will be drawn for funding in
                        Annual Plans.
                        These areas may be expressed as specific program areas (such as Allied Heath or Culinary) or
                        broader activities (such as adjunct training or developing work based learning opportunities).
                        If a specific program area is selected, any activities associated with that program would be
                        permissible. Broader areas will require more specific detail.
                    </div>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col>
                    <b-table hover
                             small
                             bordered
                             responsive="md"
                             :fields="focusAreaFields"
                             :items="theFocusAreas"
                    >
                        <template v-slot:cell(actions)="row">
                            <b-form-row>
                                <b-col class="actions-column">
                                    <b-button v-b-popover.hover.bottom="editButtonTooltip"
                                              size="sm"
                                              :class="{
                                                  'edit-button': true,
                                              }"
                                              :variant="'outline-tetradic-3'"
                                              @click.stop="modalEdit(row.item, row.index, $event.target)"
                                    >
                                        <fa-icon fixed-width size="lg" :icon="editButtonIcon" />
                                    </b-button>
                                </b-col>
                                <b-col class="actions-column">
                                    <b-button v-show="!formDisabled"
                                              :id="'areaDeleteButton-' + row.index"
                                              ref="deleteButton"
                                              v-b-popover.hover.bottom="'Delete this entry.'"
                                              :class="{
                                                  'delete-button': true,
                                              }"
                                              :variant="'outline-delete'"
                                              size="sm"
                                              @click.stop="onShowPopover(
                                                  'areaDeleteButton-' + row.index, row.item._storeIndex
                                              )"
                                    >
                                        <fa-icon fixed-width size="lg" :icon="['fal', 'trash-alt']" />
                                        <!-- <fa-icon :icon="['fas', 'trash']"></fa-icon> -->
                                    </b-button>
                                </b-col>
                            </b-form-row>

                            <b-popover ref="popoverConfirmDelete"
                                       :target="'areaDeleteButton-' + row.index"
                                       class="popover-confirm-delete"
                            >
                                <template slot="title" class="title-class">
                                    <strong>Delete Item</strong>
                                </template>
                                <template slot="default">
                                    <b-form-row>
                                        <b-col>
                                            Are you certain that you wish to delete this focus area?
                                            This is not a reversible action.
                                        </b-col>
                                    </b-form-row>
                                </template>

                                <b-form-row>
                                    <b-col ref="cancelButton" cols="6">
                                        <b-button size="sm"
                                                  block
                                                  variant="secondary"
                                                  @click="onClosePopover"
                                        >
                                            <strong>Cancel</strong>
                                        </b-button>
                                    </b-col>
                                    <b-col cols="6">
                                        <b-button size="sm"
                                                  block
                                                  class="confirm-delete-button"
                                                  @click="onDeleteOk(row)"
                                        >
                                            <strong>Delete</strong>
                                        </b-button>
                                    </b-col>
                                </b-form-row>
                            </b-popover>
                        </template>
                    </b-table>
                </b-col>
            </b-form-row>
            <br>
            <b-row>
                <b-col>
                    <b-button v-if="!formDisabled"
                              class="rounded-button"
                              size="sm"
                              block
                              style="background-color: #006438; border: 0rem;"
                              @click="modalAdd"
                    >
                        <span><fa-icon :icon="['fas', 'plus']" /> Add Focus Area</span>
                    </b-button>
                </b-col>
            </b-row>
            <br>
            <b-row>
                <b-col>
                    <LocalApplicationModuleActions
                        :is-loading="isSaving"
                        background-color="#006438"
                        :is-disabled="formDisabled"
                        @saveForm="saveForm"
                    />
                </b-col>
            </b-row>
        </fieldset>

        <!-- ============================= Focus Area Modal ============================== -->

        <template>
            <b-modal
                ref="focus-area-modal"
                title="Add Focus Area"
                :visible="modalVisible"
                static
                lazy
                no-close-on-backdrop
                no-close-on-esc
                size="lg"
                centered
                @shown="modalShownHandler"
                @hidden="modalHiddenHandler"
            >
                <div class="text-left">
                    <template>
                        <!-- Focus Area Name -->
                        <b-form-row>
                            <b-col>
                                <b-form-group id="focusAreaName"
                                              label="Focus Area Name:"
                                              horizontal
                                              label-cols="3"
                                              class="field-title"
                                >
                                    <b-form-input id="areaName"
                                                  v-model="focusAreaModalData.areaName"
                                                  :disabled="formDisabled"
                                                  type="text"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-form-row>
                        <br>

                        <!-- Focus Area Description -->
                        <b-form-row>
                            <b-col>
                                <b-form-group id="focusAreaDescription"
                                              label="Focus Area Description:"
                                              horizontal
                                              label-cols="3"
                                              class="field-title"
                                >
                                    <b-form-input id="Description"
                                                  v-model="focusAreaModalData.description"
                                                  :disabled="formDisabled"
                                                  type="text"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-form-row>
                        <br>

                        <!-- Focus Area Location -->
                        <b-form-row>
                            <b-col>
                                <b-form-group id="focusAreaLocation"
                                              label="Focus Area Location:"
                                              horizontal
                                              label-cols="3"
                                              class="field-title"
                                >
                                    <b-form-input id="location"
                                                  v-model="focusAreaModalData.location"
                                                  :disabled="formDisabled"
                                                  type="text"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-form-row>
                        <br>
                    </template>
                </div>

                <template slot="modal-footer">
                    <b-button size="sm"
                              variant="outline-secondary"
                              @click="modalCancelledHandler"
                    >
                        <fa-icon :icon="['fas', 'chevron-left']" /> Cancel
                    </b-button>
                    <b-button v-b-tooltip.hover
                              size="sm"
                              :disabled="!modalSimpleValidation"
                              title="Please enter information in each field above to enable SAVE button."
                              variant="primary"
                              @click="modalOkHandler"
                    >
                        <fa-icon :icon="['fas', 'save']" /> Save
                    </b-button>
                </template>
            </b-modal>
        </template>
    </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { Log } from '$Shared/@arc/vue-arc';


const storeModuleName = 'comprehensiveNeedsAssessmentSummary';
// const storeModuleNamespace = 'localApplications/module';

const focusAreaFields = [
    {
        key: 'areaName',
        label: 'Name',
        sortable: true,
    },
    {
        key: 'description',
        label: 'Description',
        sortable: true,
    },
    {
        key: 'location',
        label: 'Location',
        sortable: true,
    },
    {
        key: 'actions',
        label: 'Actions',
        sortable: false,
    },
];

const emptyFormData = {
    areaName: null,
    description: null,
    location: null,
};

export default {
    name: 'ComprehensiveNeedsAssessmentSummary',

    props: {
        // formDisabled: { type: Boolean, default: true }
    },

    data() {
        return {
            focusAreaFields,
            isSaving: false,
            modalVisible: false,
            focusAreaModalData: {
                ...emptyFormData,
            },
            rowData: {},
            moduleTitle: 'Comprehensive Needs Assessment Results Summary',
            nextModule: { name: 'localApplication.CteOfferings' },
            // previousModule: { name: 'localApplication.History' },
            previousModule: { name: 'localApplication.Section134Instructions' },
        };
    },

    computed: {
        ...mapState('localApplication', {
            currentState: (state) => state.localApplicationData.state.currentState,
        }),

        ...mapGetters({
            isAuthorized: 'auth/isAuthorized',
            localApplicationModuleContent: `localApplication/module/${storeModuleName}/content`,
        }),

        formDisabled() {
            return !this.isAuthorized('update', 'local_application', this.currentState);
        },

        prioritized: {
            get() { return this.localApplicationModuleContent.prioritized; },
            set(value) { this.updateContentField({ field: 'prioritized', value }); }
        },

        studentPerformanceAnalysis: {
            get() { return this.localApplicationModuleContent.studentPerformanceAnalysis; },
            set(value) { this.updateContentField({ field: 'studentPerformanceAnalysis', value }); }
        },

        otherNeedsAssessment: {
            get() { return this.localApplicationModuleContent.otherNeedsAssessment; },
            set(value) { this.updateContentField({ field: 'otherNeedsAssessment', value }); }
        },

        theFocusAreas: {
            get() { return this.localApplicationModuleContent.theFocusAreas; },
            set(value) { this.updateContentField({ field: 'theFocusAreas', value }); }
        },

        editButtonIcon() {
            return (this.formDisabled) ? ['fas', 'info-circle'] : ['fas', 'pencil'];
        },

        editButtonTooltip() {
            return (this.formDisabled)
                ? 'Click to view this focus area entry.'
                : 'Click to edit this focus area entry.';
        },

        areaNameValidate() {
            return this.focusAreaModalData.areaName !== null && this.focusAreaModalData.areaName !== '';
        },

        descriptionValidate() {
            return this.focusAreaModalData.description !== null && this.focusAreaModalData.description !== '';
        },

        locationValidate() {
            return this.focusAreaModalData.location !== null && this.focusAreaModalData.location !== '';
        },

        modalSimpleValidation() {
            return this.areaNameValidate && this.descriptionValidate && this.locationValidate;
        },
    },

    methods: {
        ...mapActions(`localApplication/module/${storeModuleName}`, {
            saveLocalApplicationModule: 'save',
            updateContentField: 'updateContentField',
        }),

        modalEdit(item, idx) {
            this.rowData = {
                ...item,
                index: idx + 1,
            };

            this.modalVisible = true;
        },

        onShowPopover(targetID, idx) {
            this.$root.$emit('bv::show::popover', targetID);
        },

        onClosePopover() {
            this.$root.$emit('bv::hide::popover');
        },

        onDeleteOk(row) {
            this.theFocusAreas.splice(row.index, 1);

            this.$root.$emit('bv::hide::popover');
        },

        modalAdd() {
            this.focusAreaModalData = { ...emptyFormData };
            this.rowData = { ...emptyFormData };
            this.modalVisible = true;
        },

        modalOkHandler(bvEvt) {
            console.log('+++++ modalOkHandler bvEvt: ', bvEvt);
            console.log('+++++ modalOkHandler focusAreaModalData: ', this.focusAreaModalData);
            if (this.focusAreaModalData.index) {
                this.theFocusAreas.forEach((item, idx) => {
                    let itemIdx = idx + 1;
                    if (itemIdx === this.focusAreaModalData.index) {
                        item.areaName = this.focusAreaModalData.areaName;
                        item.description = this.focusAreaModalData.description;
                        item.location = this.focusAreaModalData.location;
                    }
                });
            } else {
                this.theFocusAreas.push(this.focusAreaModalData);
            }

            this.focusAreaModalData = { ...emptyFormData };
            this.rowData = { ...emptyFormData };
            this.modalHiddenHandler();
        },

        modalShownHandler() {
            this.focusAreaModalData = (this.rowData)
                ? (this.rowData)
                : { ...emptyFormData };
        },

        modalHiddenHandler() {
            this.modalVisible = false;
            this.focusAreaModalData = { ...emptyFormData };
        },

        modalCancelledHandler() {
            this.focusAreaModalData = { ...emptyFormData };
            this.modalHiddenHandler();
        },

        async saveForm() {
            this.isSaving = true;
            try {
                const response = await this.saveLocalApplicationModule();
                this.isSaving = false;
                Log.debug(response, `COMPONENT:${this.name} -> Save module response:`);
            } catch (error) {
                this.isSaving = false;
            }
        },
    }
};
</script>
